import React from 'react';
import Icon from '@/components/ui/Icon';
import Pill from '@/components/ui/Pill';
import cn from '@/utils/cn';
import stripStandardsAliasPrefix from '@/utils/stripStandardsAliasPrefix';
import CorrelatedContentPanel from './CorrelatedContentPanel';
import CorrelationCountPill from './CorrelationCountPill';
import HighlightMatch from './HighlightMatch';
import { useStandardsContent } from './standardsContentContext';
import { ChildrenMap } from './standardsTreeUtils';
import { StandardNode } from './types';

const LEVEL_PILL_COLORS: Record<number, string> = {
  1: 'purple',
  2: 'blue',
  3: 'green',
  4: 'orange',
  5: 'pink',
  6: 'grey',
};

interface StandardTreeNodeProps {
  childrenMap: ChildrenMap;
  correlationCountMap: Map<number, number>;
  expandedNodes: Set<number>;
  gradeLabel?: string;
  matchIds?: Set<number>;
  node: StandardNode;
  searchQuery?: string;
  showCorrelationCounts: boolean;
  visibleIds?: Set<number>;
  onToggleNode: (id: number) => void;
}

const StandardTreeNode = ({
  childrenMap,
  correlationCountMap,
  expandedNodes,
  gradeLabel,
  matchIds,
  node,
  searchQuery = '',
  showCorrelationCounts,
  visibleIds,
  onToggleNode,
}: StandardTreeNodeProps) => {
  const { expandedContentLeaves, toggleContentLeaf } = useStandardsContent();
  const isSearching = searchQuery.trim().length > 0;
  const isExpanded = isSearching ? visibleIds?.has(node.id) : expandedNodes.has(node.id);
  const allChildren = childrenMap.get(node.id) ?? [];
  const children = isSearching && visibleIds
    ? allChildren.filter(c => visibleIds.has(c.id))
    : allChildren;
  const depth = (node.level ?? 1) - 1;
  const description = stripStandardsAliasPrefix(node.description_en, node.alias);
  const levelColor = LEVEL_PILL_COLORS[node.level ?? 1] ?? LEVEL_PILL_COLORS[6];

  // A TAGGABLE leaf with correlations expands to show its content (M1.2). Zero-count leaves and
  // search results don't expand.
  const directCount = correlationCountMap.get(node.id) ?? 0;
  const canExpandContent = !node.has_children && directCount > 0 && !isSearching;
  const contentOpen = canExpandContent && expandedContentLeaves.has(node.id);
  const interactive = (node.has_children && !isSearching) || canExpandContent;
  const chevronOpen = node.has_children ? !!isExpanded : contentOpen;
  const showChevron = isSearching ? node.has_children && children.length > 0 : node.has_children || canExpandContent;

  const handleRowClick = () => {
    if (isSearching) return;
    if (node.has_children) onToggleNode(node.id);
    else if (canExpandContent) toggleContentLeaf(node.id);
  };

  return (
    <div className="tw-overflow-hidden">
      {/* Row click is a convenience — keyboard a11y is on the chevron <button> inside */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={cn(
          'tw-flex tw-items-start tw-gap-2 tw-py-1.5 tw-pr-3 tw-text-left',
          interactive ? 'tw-cursor-pointer' : 'tw-cursor-default',
          'hover:tw-bg-gray-50',
        )}
        style={{ paddingLeft: `${12 + depth * 18}px` }}
        onClick={handleRowClick}
      >
        <button
          aria-expanded={interactive ? chevronOpen : undefined}
          aria-label={interactive ? `${chevronOpen ? 'Collapse' : 'Expand'} ${node.alias || node.id}` : undefined}
          className={cn(
            'tw-mt-0.5 tw-flex-shrink-0 tw-w-4 tw-inline-flex tw-bg-transparent tw-border-0 tw-p-0',
            interactive ? 'tw-cursor-pointer' : 'tw-cursor-default',
            !showChevron && 'tw-invisible',
          )}
          tabIndex={interactive ? 0 : -1}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick();
          }}
        >
          <Icon icon={chevronOpen ? 'ChevronDown' : 'ChevronRight'} size={12} />
        </button>

        <span className="tw-text-xs tw-text-dark-grey tw-font-mono tw-flex-shrink-0 tw-mt-0.5">
          ID: {isSearching
          ? <HighlightMatch query={searchQuery} text={String(node.id)} />
          : node.id}
        </span>

        {node.alias && (
          <span className="tw-text-sm tw-font-mono tw-font-bold tw-flex-shrink-0 tw-mt-px">
            {isSearching ? <HighlightMatch query={searchQuery} text={node.alias} /> : node.alias}
          </span>
        )}

        <span className="tw-text-sm tw-flex-1 tw-min-w-0">
          {isSearching ? <HighlightMatch query={searchQuery} text={description} /> : description}
        </span>

        {showCorrelationCounts && (
          <span className="tw-flex-shrink-0 tw-mt-0.5">
            <CorrelationCountPill count={directCount} direct={!node.has_children} />
          </span>
        )}

        <span
          className="tw-flex-shrink-0 tw-mt-0.5"
          style={node.has_children ? { visibility: 'hidden' as const } : undefined}
        >
          <Pill pillColor="green">TAGGABLE</Pill>
        </span>

        <div className="tw-flex-shrink-0 tw-mt-px">
          <Pill solid pillColor={levelColor}>L{node.level ?? 1}</Pill>
        </div>
      </div>

      {contentOpen && <CorrelatedContentPanel gradeLabel={gradeLabel} standardId={node.id} />}

      {isExpanded && children.map(child => (
        <StandardTreeNode
          key={child.id}
          childrenMap={childrenMap}
          correlationCountMap={correlationCountMap}
          expandedNodes={expandedNodes}
          gradeLabel={gradeLabel}
          matchIds={matchIds}
          node={child}
          searchQuery={searchQuery}
          showCorrelationCounts={showCorrelationCounts}
          visibleIds={visibleIds}
          onToggleNode={onToggleNode}
        />
      ))}
    </div>
  );
};

export default StandardTreeNode;
